/**
 * Calculates the relative luminance of a color according to WCAG guidelines
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Relative luminance (0-1)
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
    // Convert to 0-1 range
    const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Converts hex color to RGB values
 * @param hex Hex color string (e.g., "#FF5733" or "FF5733")
 * @returns RGB object or null if invalid
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const cleanHex = hex.replace("#", "");

    if (cleanHex.length === 3) {
        // Short hex format (e.g., "F53")
        const r = Number.parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = Number.parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = Number.parseInt(cleanHex[2] + cleanHex[2], 16);
        return { r, g, b };
    } else if (cleanHex.length === 6) {
        // Full hex format (e.g., "FF5733")
        const r = Number.parseInt(cleanHex.substring(0, 2), 16);
        const g = Number.parseInt(cleanHex.substring(2, 4), 16);
        const b = Number.parseInt(cleanHex.substring(4, 6), 16);
        return { r, g, b };
    }

    return null;
}

/**
 * Parses various color formats to RGB
 * @param color Color string (hex, rgb, rgba, or Material-UI color)
 * @returns RGB object or null if parsing fails
 */
function parseColor(color: string): { r: number; g: number; b: number } | null {
    // Handle hex colors
    if (color.startsWith("#") || /^[0-9A-Fa-f]{3,6}$/.test(color)) {
        return hexToRgb(color);
    }

    // Handle rgb/rgba colors
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
        return {
            r: Number.parseInt(rgbMatch[1]),
            g: Number.parseInt(rgbMatch[2]),
            b: Number.parseInt(rgbMatch[3])
        };
    }

    return null;
}

/**
 * Determines the best contrast color (black or white) for a given background color
 * @param backgroundColor Background color in hex, rgb, or rgba format
 * @param threshold Luminance threshold (default: 0.5, lower = prefer white text)
 * @returns "black" or "white" for optimal contrast
 */
export function getContrastColor(backgroundColor: string, threshold: number = 0.5): "black" | "white" {
    const rgb = parseColor(backgroundColor);

    if (!rgb) {
        // Fallback to black if color parsing fails
        console.warn(`Unable to parse color: ${backgroundColor}`);
        return "black";
    }

    const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);

    // If luminance is above threshold, use black text; otherwise use white
    return luminance > threshold ? "black" : "white";
}

/**
 * Gets the contrast color with full color values
 * @param backgroundColor Background color
 * @param threshold Luminance threshold
 * @returns Object with color values
 */
export function getContrastColorValues(
    backgroundColor: string,
    threshold: number = 0.5
): { color: string; hex: string } {
    const contrastColor = getContrastColor(backgroundColor, threshold);

    return {
        color: contrastColor,
        hex: contrastColor === "black" ? "#000000" : "#ffffff"
    };
}
